import { StatusWrapper } from '@component/StatusWrapper/StatusWrapper';
import { UploadArrowTopIcon } from '@svg/UploadArrowTopIcon';
import { ImageUploadPropsType } from '@uikit/FormItems/constants';
import React, {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { handleImgError } from '@src/utils';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { IMAGE_STORAGE_URL } from '@src/utils/constants';
import { toastMessage } from '@utils/toastMessage';

const ImageUpload: FC<ImageUploadPropsType> = ({
  currentImage,
  selectedImage,
  fallBackImage,
  imgClassName,
  imgWrapperClassName,
  onlyImage = false,
  status = 'none',
  setValue,
  enableCrop = true,
  path,
  dataTestId = 'image-upload',
  maxHeight,
  alignRight = false,
}) => {
  const [imageURL, setImageURL] = useState<string>(currentImage);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const objectUrlRef = useRef<string | null>(null);
  const confirmedSnapshotRef = useRef<{
    file: File | null;
    url: string;
    objectUrl: string | null;
  } | null>(null);

  const setPreviewUrl = useCallback((nextUrl: string) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    objectUrlRef.current = nextUrl.startsWith('blob:') ? nextUrl : null;
    setImageURL(nextUrl);
  }, []);

  useEffect(() => {
    if (!objectUrlRef.current) {
      setImageURL(currentImage);
    }
  }, [currentImage]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      if (confirmedSnapshotRef.current?.objectUrl) {
        URL.revokeObjectURL(confirmedSnapshotRef.current.objectUrl);
      }
    };
  }, []);

  const createFileWithTimestamp = (file: File) => {
    const ext = file.name.split('.').pop() || '';
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    const stamp = Date.now();
    const safeExt = ext ? `.${ext}` : '';
    return new File([file], `${nameWithoutExt}_${stamp}${safeExt}`, {
      type: file.type,
    });
  };

  const handleImageUpload = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    fileUploadRef.current?.click();
  };

  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSizeMB = 1.5;
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(toastMessage.COMMON.fileSizeTooLarge);
      e.target.value = '';
      return;
    }

    const renamedFile = createFileWithTimestamp(file);

    if (enableCrop) {
      confirmedSnapshotRef.current = {
        file: selectedFile,
        url: imageURL,
        objectUrl: objectUrlRef.current,
      };
      objectUrlRef.current = null;
    }

    setSelectedFile(renamedFile);
    setPreviewUrl(URL.createObjectURL(renamedFile));

    if (enableCrop) {
      setIsCropModalOpen(true);
    } else {
      setValue?.('image', renamedFile as any, { shouldDirty: true });
    }
    e.target.value = '';
  };

  const onImageLoaded = useCallback((img: HTMLImageElement) => {
    imgRef.current = img;

    const smallerSide = Math.min(img.naturalWidth, img.naturalHeight);
    const cropSize = smallerSide / 2;

    const widthPercent = (cropSize / img.naturalWidth) * 100;
    const heightPercent = (cropSize / img.naturalHeight) * 100;
    const xPercent =
      ((img.naturalWidth - cropSize) / 2 / img.naturalWidth) * 100;
    const yPercent =
      ((img.naturalHeight - cropSize) / 2 / img.naturalHeight) * 100;

    const centeredCrop: Crop = {
      unit: '%',
      x: xPercent,
      y: yPercent,
      width: widthPercent,
      height: heightPercent,
    };

    setCrop(centeredCrop);
    setCompletedCrop(null);
    return false;
  }, []);

  const getCroppedBlob = async (): Promise<Blob | null> => {
    if (!completedCrop || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  };

  const handleCropSave = async () => {
    const blob = await getCroppedBlob();
    if (!blob) return;

    const originalName =
      selectedFile?.name?.replace(/\.[^/.]+$/, '') || 'image';
    const croppedFile = new File([blob], `${originalName}.png`, {
      type: 'image/png',
    });

    setIsCropModalOpen(false);
    setSelectedFile(croppedFile);
    setPreviewUrl(URL.createObjectURL(croppedFile));

    if (confirmedSnapshotRef.current?.objectUrl) {
      URL.revokeObjectURL(confirmedSnapshotRef.current.objectUrl);
    }
    confirmedSnapshotRef.current = null;

    setValue?.('image', croppedFile as any, { shouldDirty: true });
  };

  const handleCropCancel = () => {
    setIsCropModalOpen(false);
    setCompletedCrop(null);

    const snapshot = confirmedSnapshotRef.current;
    confirmedSnapshotRef.current = null;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    if (snapshot) {
      setSelectedFile(snapshot.file);
      objectUrlRef.current = snapshot.objectUrl;
      setImageURL(snapshot.url);
      return;
    }

    const next =
      currentImage || fallBackImage || `${IMAGE_STORAGE_URL}/${''}` || '';
    setSelectedFile(null);
    setImageURL(next);
  };

  const handlePreviewError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      if (
        fallBackImage &&
        event.currentTarget.dataset.fallbackApplied !== 'true'
      ) {
        event.currentTarget.dataset.fallbackApplied = 'true';
        event.currentTarget.src = fallBackImage;
        return;
      }

      handleImgError(event);
    },
    [fallBackImage]
  );

  const handlePreviewLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    delete event.currentTarget.dataset.fallbackApplied;
  };

  const previewURL =
    selectedImage === undefined || selectedImage instanceof Blob
      ? imageURL
      : currentImage;

  const imagePreview = (
    <img
      style={{ maxHeight, width: alignRight ? 'auto' : '100%' }}
      src={previewURL}
      alt="upload"
      className={`uploading-image ${imgClassName || ''}`}
      onError={handlePreviewError}
      onLoad={handlePreviewLoad}
      data-test-id={`${dataTestId}-image`}
    />
  );

  return (
    <div className="image-upload-wrapper">
      {!onlyImage && (
        <form
          id="form"
          encType="multipart/form-data"
          data-test-id={`${dataTestId}-form`}
        >
          <button
            data-test-id={`${dataTestId}-submit-button`}
            type="submit"
            onClick={handleImageUpload}
            disabled={onlyImage}
            className="change-img-v2"
          >
            <UploadArrowTopIcon />
          </button>
          <input
            data-test-id={`${dataTestId}-file-input`}
            type="file"
            ref={fileUploadRef}
            onChange={onFileSelect}
            hidden
            accept="image/*"
          />
        </form>
      )}

      <div
        className={`uploading-image-wrapper ${imgWrapperClassName || ''}`}
        style={{ maxHeight, display: 'flex', justifyContent: 'flex-end' }}
      >
        {status !== 'none' ? (
          <StatusWrapper size={30} statusColor={status} dataTestId={dataTestId}>
            {imagePreview}
          </StatusWrapper>
        ) : (
          imagePreview
        )}
      </div>

      {isCropModalOpen && (
        <div
          className="crop-modal-overlay"
          data-test-id={`${dataTestId}-crop-modal-overlay`}
        >
          <div className="crop-modal">
            <h3>Crop Image</h3>

            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              keepSelection={true}
            >
              <img
                ref={imgRef}
                src={imageURL}
                alt="crop source"
                onLoad={(e) => onImageLoaded(e.currentTarget)}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  width: 400,
                  height: 'auto',
                  objectFit: 'contain',
                }}
                data-test-id={`${dataTestId}-crop-image-preview`}
              />
            </ReactCrop>

            <div className="crop-modal-buttons">
              <button
                data-test-id={`${dataTestId}-crop-modal-save-button`}
                type="button"
                onClick={handleCropSave}
                className="btn btn-primary"
              >
                Crop & Save
              </button>
              <button
                data-test-id={`${dataTestId}-crop-modal-cancel-button`}
                type="button"
                onClick={handleCropCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
